import { useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { compact } from "lodash-es"
import {
  CustomerBankAccountMandateStateEnumType,
  B2BPaymentMethod,
  PaymentMethodState,
  CorePaymentMethod,
} from "@oysterhr/schema"
import { DotDropdown, type DropdownValue } from "@oysterhr/feature-components"

// modals
import { NewCompanyPaymentMethodCardLayout } from "../NewCompanyPaymentMethodCardLayout"
import { PaymentMethodEnumType } from "~/app/utils/constants"
import { useNavigate } from "react-router-dom"
import { companySepaMandatePath } from "@oysterhr/router"
import { isAchAndVerifiable, isAuthorizable, hasCreditorId } from "../../utils"
import { MandateSign } from "../../sepa/hooks/useMandateSign"
import { CreditorsIdModal } from "../../modals/CreditorsIdModal"

interface NewCompanyPaymentMethodCardProps {
  paymentMethod: CorePaymentMethod | B2BPaymentMethod
  companyId: string
  onDelete: (paymentMethod: CorePaymentMethod | B2BPaymentMethod) => void
  onAuthorize: (paymentMethod: CorePaymentMethod | B2BPaymentMethod) => void
  setShowConfirmSharingModal?: (showModal: boolean) => void
  mandateSign?: MandateSign
}

export const NewCompanyPaymentMethodCard = ({
  paymentMethod,
  companyId,
  mandateSign,
  onAuthorize,
  setShowConfirmSharingModal,
  onDelete,
}: NewCompanyPaymentMethodCardProps) => {
  const { t } = useTranslation(
    "company_interface/CompanyPaymentMethodsPage/CompanyBankPaymentMethodCard",
  )
  const navigate = useNavigate()
  const [showCreditorsIdModal, setShowCreditorsIdModal] = useState(false)

  const openConfirmSharingModal = useCallback(() => {
    setShowConfirmSharingModal?.(true)
  }, [setShowConfirmSharingModal])

  const onViewSignedMandate = useCallback(() => {
    navigate(companySepaMandatePath(companyId))
  }, [navigate, companyId])

  const actions = useMemo<DropdownValue[]>(() => {
    const { onMandateSign, isSigning } = mandateSign ?? {}

    const isSepaCustomerBankAccount =
      paymentMethod.__typename === PaymentMethodEnumType.CUSTOMER_BANK_ACCOUNT_SEPA

    // Contractor payment method is created as Verified and Sepa FTE is created as In verification

    const canSignMandate =
      isSepaCustomerBankAccount &&
      onMandateSign &&
      paymentMethod.state === PaymentMethodState.AuthorizedByUser &&
      paymentMethod.mandate?.state === CustomerBankAccountMandateStateEnumType.Initialized &&
      !paymentMethod.userNotSigner

    const canLodgeMandate =
      isSepaCustomerBankAccount &&
      paymentMethod.mandate?.state === CustomerBankAccountMandateStateEnumType.Signed

    const canViewSignedMandate =
      isSepaCustomerBankAccount &&
      paymentMethod.mandate?.state &&
      [
        CustomerBankAccountMandateStateEnumType.Signed,
        CustomerBankAccountMandateStateEnumType.Lodged,
        CustomerBankAccountMandateStateEnumType.Active,
      ].includes(paymentMethod?.mandate?.state)

    return compact([
      hasCreditorId(paymentMethod) && {
        title: t("actions.seeCreditorsId"),
        onClick: () => setShowCreditorsIdModal(true),
      },
      isAuthorizable(paymentMethod) && {
        title: t("actions.authorize"),
        onClick: () => onAuthorize(paymentMethod),
      },
      isAchAndVerifiable(paymentMethod) && {
        title: t("actions.verify"),
        onClick: () => {
          navigate("verify-stripe-ach-payment-method", {
            state: {
              paymentMethod: { ...paymentMethod },
            },
          })
        },
      },
      canSignMandate && {
        title: t("actions.signMandate"),
        disabled: isSigning,
        onClick: () => onMandateSign(companyId, false),
      },
      canLodgeMandate && {
        title: t("actions.lodgeMandate"),
        onClick: openConfirmSharingModal,
      },
      canViewSignedMandate && {
        title: t("actions.viewSignedMandate"),
        onClick: onViewSignedMandate,
      },
      {
        title: t("actions.delete"),
        danger: true,
        onClick: () => onDelete(paymentMethod),
      },
    ])
  }, [
    onAuthorize,
    onDelete,
    openConfirmSharingModal,
    onViewSignedMandate,
    t,
    navigate,
    mandateSign,
    paymentMethod,
    companyId,
  ])

  return (
    <div>
      <NewCompanyPaymentMethodCardLayout paymentMethod={paymentMethod}>
        {/* Actions in the dropdown */}
        <DotDropdown
          dropdownValues={actions}
          buttonSize="sm"
          ariaLabel={"actions"}
          containerStyle="w-auto whitespace-nowrap"
        />
      </NewCompanyPaymentMethodCardLayout>

      <CreditorsIdModal
        paymentMethod={paymentMethod}
        isOpen={showCreditorsIdModal}
        closeModal={() => setShowCreditorsIdModal(false)}
      />
    </div>
  )
}
